/**
 * Visual docking guidance — the display at the head of each stand that
 * brings the aircraft in on the centreline and stops it on the mark.
 *
 * One record per stand, grouped by the apron the stand sits on, because an
 * apron is how the ramp is run and how a fault is reported. The record keeps
 * what the unit is, whether it is guiding, when it was last calibrated, which
 * aircraft types it has been taught, and how its dockings went.
 *
 * The question this answers for the ramp is not "is the unit green?" but
 * "does the next aircraft onto this stand need a marshaller?" — and that is
 * yes in three cases, each named:
 *
 *  1. **The unit is not guiding** — a fault, or out for maintenance.
 *  2. **There is no unit** — a remote stand marshalled by hand, or a stand
 *     never fitted.
 *  3. **The type is not in its database.** A unit guiding perfectly well
 *     will still refuse, or worse misidentify, an aircraft it was never
 *     taught; the stop mark for a B77W is not the stop mark for an A320.
 *
 * Calibration is due on an interval the airport sets. The 180 days here is a
 * proposed default, not a manufacturer's figure; each unit may carry its own.
 */
import { t } from "../i18n";
import type { Flight, SizeCode } from "./flights";
import type { Stand, StandRegister } from "./stands";

export type VdgsKind = "a_vdgs" | "vdgs" | "marshaller" | "none";
export type VdgsStatus = "in_service" | "degraded" | "fault" | "maintenance" | "not_fitted";
export type DockResult = "ok" | "short" | "overrun" | "abort" | "marshalled";

export const KIND_LABEL: Record<VdgsKind, string> = {
  a_vdgs: "Advanced VDGS (laser)",
  vdgs: "VDGS (parallax / optical)",
  marshaller: "Marshaller only",
  none: "Not fitted",
};

export const STATUS_LABEL: Record<VdgsStatus, string> = {
  in_service: "In service",
  degraded: "Degraded",
  fault: "Fault",
  maintenance: "Maintenance",
  not_fitted: "No unit",
};

export const RESULT_LABEL: Record<DockResult, string> = {
  ok: "On the mark",
  short: "Stopped short",
  overrun: "Overran the mark",
  abort: "Aborted",
  marshalled: "Marshalled by hand",
};

export const APRON_LABEL: Record<string, string> = {
  "APRON-1": "Main terminal apron · 101–121",
  "APRON-2": "Apron 2",
  "APRON-R": "Remote apron",
  "APRON-C": "Cargo apron",
  "APRON-G": "General aviation apron",
  "APRON-M": "Maintenance apron",
};

/** Proposed: days between calibrations, where a unit does not carry its own. */
export const CALIBRATION_DAYS = 180;
/** Proposed: how far from the stop mark still counts as on it, in centimetres. */
export const STOP_TOLERANCE_CM = 50;
/** How far ahead the ramp wants to know that a marshaller is needed. */
export const COVER_HOURS = 6;

export interface DockingEvent {
  at: string;
  flight?: string;
  aircraft: string;
  /** positive = past the mark, negative = short of it */
  stopErrorCm: number;
  /** from the aircraft entering the capture zone to STOP */
  seconds: number;
  result: DockResult;
  note?: string;
}

export interface VdgsUnit {
  stand: string;
  /**
   * Where the stand is, for a unit on a stand the stand register does not
   * carry yet — the docking estate and the allocation plan are kept by
   * different people, and a unit must not vanish because its stand has not
   * been entered there.
   */
  apron?: string;
  gate?: string;
  size?: SizeCode;
  /** the asset tag a work order is raised against */
  tag: string;
  kind: VdgsKind;
  make?: string;
  model?: string;
  status: VdgsStatus;
  /** what is wrong, when something is */
  fault?: { since: string; what: string; workOrder?: string };
  installed?: string;
  lastCalibration?: string;
  calibrationDays?: number;
  /** the emergency stop, tested from the bridge and from the ground */
  lastEStopTest?: string;
  /** the ICAO types the unit has been taught */
  aircraftTypes: string[];
  /** interlocked with the bridge: the bridge will not move until the unit shows STOP */
  bridgeInterlock?: boolean;
  dockings: DockingEvent[];
  notes?: string;
}

/** Where the docking register is kept. */
export const VDGS_TOPIC = "aerodrome.vdgs";

export interface VdgsRegister {
  units: VdgsUnit[];
}

const DAY = 86_400_000;
const ms = (iso: string | undefined) => (iso ? Date.parse(iso) : NaN);

export function unitFor(units: readonly VdgsUnit[], stand: string): VdgsUnit | undefined {
  return units.find((u) => u.stand === stand);
}

/** A unit that can bring an aircraft in by itself. */
export function guides(u: VdgsUnit | undefined): boolean {
  return !!u && (u.kind === "a_vdgs" || u.kind === "vdgs") && (u.status === "in_service" || u.status === "degraded");
}

export type CalState = "ok" | "soon" | "overdue" | "never" | "n/a";

export function calibration(u: VdgsUnit, now: Date): { state: CalState; due?: string; daysLeft?: number } {
  if (u.kind === "marshaller" || u.kind === "none") return { state: "n/a" };
  const last = ms(u.lastCalibration);
  if (Number.isNaN(last)) return { state: "never" };
  const due = last + (u.calibrationDays ?? CALIBRATION_DAYS) * DAY;
  const daysLeft = Math.floor((due - now.getTime()) / DAY);
  return {
    state: daysLeft < 0 ? "overdue" : daysLeft <= 14 ? "soon" : "ok",
    due: new Date(due).toISOString(),
    daysLeft,
  };
}

export interface UnitStats {
  dockings: number;
  onMark: number;
  aborts: number;
  overruns: number;
  marshalled: number;
  averageSeconds: number | null;
  averageErrorCm: number | null;
  /** share of guided dockings stopped within tolerance, 0–100 */
  accuracy: number | null;
}

/** The last day's dockings, as the unit logged them. */
export function unitStats(u: VdgsUnit, now: Date, hours = 24): UnitStats {
  const from = now.getTime() - hours * 3_600_000;
  const list = u.dockings.filter((d) => ms(d.at) >= from && ms(d.at) <= now.getTime());
  const guided = list.filter((d) => d.result !== "marshalled" && d.result !== "abort");
  const avg = (xs: number[]) => (xs.length ? Math.round(xs.reduce((a, b) => a + b, 0) / xs.length) : null);
  const within = guided.filter((d) => Math.abs(d.stopErrorCm) <= STOP_TOLERANCE_CM).length;
  return {
    dockings: list.length,
    onMark: list.filter((d) => d.result === "ok").length,
    aborts: list.filter((d) => d.result === "abort").length,
    overruns: list.filter((d) => d.result === "overrun").length,
    marshalled: list.filter((d) => d.result === "marshalled").length,
    averageSeconds: avg(guided.map((d) => d.seconds)),
    averageErrorCm: avg(guided.map((d) => Math.abs(d.stopErrorCm))),
    accuracy: guided.length ? Math.round((within / guided.length) * 100) : null,
  };
}

export interface OnStand {
  flight: Flight;
  from: string;
  to: string;
}

/** Who is on the stand now, and who comes next — arrivals are what a docking unit brings in. */
export function standTraffic(
  stand: string,
  register: StandRegister,
  flights: readonly Flight[],
  now: Date,
): { now?: OnStand; next?: OnStand } {
  const t0 = now.getTime();
  const rows = register.assignments
    .filter((a) => a.standCode === stand)
    .map((a) => ({ a, flight: flights.find((f) => f.id === a.flightId) }))
    .filter((r): r is { a: (typeof register.assignments)[number]; flight: Flight } => !!r.flight && !r.flight.cancelled)
    .sort((x, y) => ms(x.a.from) - ms(y.a.from));
  const cur = rows.find((r) => ms(r.a.from) <= t0 && ms(r.a.to) > t0);
  const nxt = rows.find((r) => ms(r.a.from) > t0);
  return {
    now: cur ? { flight: cur.flight, from: cur.a.from, to: cur.a.to } : undefined,
    next: nxt ? { flight: nxt.flight, from: nxt.a.from, to: nxt.a.to } : undefined,
  };
}

export type CoverWhy = "not_guiding" | "no_unit" | "type_unknown";

export const COVER_LABEL: Record<CoverWhy, string> = {
  not_guiding: "The docking unit is not guiding",
  no_unit: "No docking unit on this stand",
  type_unknown: "This aircraft type is not in the unit's database",
};

export interface CoverNeed {
  stand: string;
  apron: string;
  flight: Flight;
  at: string;
  why: CoverWhy;
  unit?: VdgsUnit;
}

/**
 * Every arrival in the next few hours that will need a marshaller to bring
 * it on stand. A stand that has always been marshalled is still listed: the
 * marshaller has to be rostered whether or not anything is broken.
 */
export function marshallerCover(
  register: StandRegister,
  flights: readonly Flight[],
  units: readonly VdgsUnit[],
  now: Date,
  hours = COVER_HOURS,
): CoverNeed[] {
  const until = now.getTime() + hours * 3_600_000;
  const out: CoverNeed[] = [];
  for (const a of register.assignments) {
    const from = ms(a.from);
    if (a.actual || from <= now.getTime() || from > until) continue;
    const flight = flights.find((f) => f.id === a.flightId);
    if (!flight || flight.cancelled || flight.direction !== "arrival") continue;
    const stand = register.stands.find((s) => s.code === a.standCode);
    const unit = unitFor(units, a.standCode);
    let why: CoverWhy | null = null;
    if (!unit || unit.kind === "marshaller" || unit.kind === "none" || unit.status === "not_fitted") why = "no_unit";
    else if (!guides(unit)) why = "not_guiding";
    else if (flight.aircraftIcao && !unit.aircraftTypes.includes(flight.aircraftIcao)) why = "type_unknown";
    if (why) out.push({ stand: a.standCode, apron: stand?.zoneCode ?? "", flight, at: a.from, why, unit });
  }
  return out.sort((x, y) => ms(x.at) - ms(y.at));
}

export type Attention = "good" | "watch" | "act";

/** What a supervisor should notice about one unit, worst first. */
export function attention(u: VdgsUnit | undefined, now: Date): { level: Attention; reasons: string[] } {
  if (!u || u.kind === "marshaller" || u.kind === "none") return { level: "good", reasons: [] };
  const reasons: string[] = [];
  let level: Attention = "good";
  const raise = (l: Attention) => {
    if (l === "act" || (l === "watch" && level === "good")) level = l;
  };
  if (u.status === "fault") {
    raise("act");
    reasons.push(u.fault?.what ? t("Fault: {what}", { what: u.fault.what }) : t("Reported faulty"));
  }
  if (u.status === "maintenance") {
    raise("watch");
    reasons.push(t("Out for maintenance"));
  }
  if (u.status === "degraded") {
    raise("watch");
    reasons.push(u.fault?.what ? t("Degraded: {what}", { what: u.fault.what }) : t("Guiding, with a reported problem"));
  }
  const cal = calibration(u, now);
  if (cal.state === "overdue") {
    raise("act");
    reasons.push(t("Calibration overdue by {n} days", { n: -(cal.daysLeft ?? 0) }));
  } else if (cal.state === "never") {
    raise("watch");
    reasons.push(t("No calibration on record"));
  } else if (cal.state === "soon") {
    raise("watch");
    reasons.push(t("Calibration due in {n} days", { n: cal.daysLeft ?? 0 }));
  }
  const st = unitStats(u, now);
  if (st.aborts >= 2) {
    raise("act");
    reasons.push(t("{n} aborted dockings in 24 hours", { n: st.aborts }));
  } else if (st.aborts === 1) {
    raise("watch");
    reasons.push(t("An aborted docking in 24 hours"));
  }
  if (st.accuracy != null && st.accuracy < 80) {
    raise("watch");
    reasons.push(t("Only {p}% of dockings on the mark", { p: st.accuracy }));
  }
  return { level, reasons };
}

export interface ApronGroup {
  apron: string;
  label: string;
  stands: Stand[];
}

/** The stands, apron by apron, in the register's own order. */
export function byApron(stands: readonly Stand[]): ApronGroup[] {
  const groups = new Map<string, Stand[]>();
  for (const s of stands) {
    const k = s.zoneCode ?? "—";
    groups.set(k, [...(groups.get(k) ?? []), s]);
  }
  const order = (a: Stand, b: Stand) => a.code.localeCompare(b.code, "en", { numeric: true });
  return [...groups.entries()].map(([apron, list]) => ({
    apron,
    label: APRON_LABEL[apron] ?? apron,
    stands: [...list].sort(order),
  }));
}

/**
 * The stands the docking screen shows: the stand register's, and any stand a
 * unit sits on that the register does not carry yet.
 */
export function dockingStands(stands: readonly Stand[], units: readonly VdgsUnit[]): Stand[] {
  const extra = units
    .filter((u) => !stands.some((s) => s.code === u.stand))
    .map((u): Stand => ({
      code: u.stand,
      name: `Stand ${u.stand}`,
      kind: u.gate ? "contact" : "remote",
      maxSize: u.size ?? "C",
      zoneCode: u.apron,
      bridge: !!u.bridgeInterlock,
      gate: u.gate,
    }));
  return [...stands, ...extra];
}

/** What the display at the head of the stand is showing right now. */
export function displayText(u: VdgsUnit | undefined, traffic: { now?: OnStand; next?: OnStand }, now: Date): string {
  if (!u || u.kind === "marshaller" || u.kind === "none" || u.status === "not_fitted") return "";
  if (u.status === "fault") return "ERR";
  if (u.status === "maintenance") return "OFF";
  if (traffic.now) {
    const on = traffic.now.flight.direction === "arrival" && now.getTime() - ms(traffic.now.from) < 5 * 60_000;
    return on ? "STOP" : "OK";
  }
  if (traffic.next && ms(traffic.next.from) - now.getTime() < 15 * 60_000)
    return traffic.next.flight.aircraftIcao ?? "WAIT";
  return "SBY";
}

export interface VdgsSummary {
  stands: number;
  fitted: number;
  guiding: number;
  down: number;
  calibrationDue: number;
  aborts: number;
  dockings: number;
  accuracy: number | null;
}

export function vdgsSummary(stands: readonly Stand[], units: readonly VdgsUnit[], now: Date): VdgsSummary {
  const fitted = units.filter((u) => (u.kind === "a_vdgs" || u.kind === "vdgs") && u.status !== "not_fitted");
  const stats = fitted.map((u) => unitStats(u, now));
  const guided = stats.reduce((a, s) => a + (s.accuracy != null ? s.dockings - s.aborts - s.marshalled : 0), 0);
  const onMark = fitted.reduce(
    (a, u) =>
      a +
      u.dockings.filter(
        (d) =>
          ms(d.at) >= now.getTime() - DAY &&
          ms(d.at) <= now.getTime() &&
          d.result !== "abort" &&
          d.result !== "marshalled" &&
          Math.abs(d.stopErrorCm) <= STOP_TOLERANCE_CM,
      ).length,
    0,
  );
  return {
    stands: stands.length,
    fitted: fitted.length,
    guiding: fitted.filter(guides).length,
    down: fitted.filter((u) => u.status === "fault" || u.status === "maintenance").length,
    calibrationDue: fitted.filter((u) => ["overdue", "soon", "never"].includes(calibration(u, now).state)).length,
    aborts: stats.reduce((a, s) => a + s.aborts, 0),
    dockings: stats.reduce((a, s) => a + s.dockings, 0),
    accuracy: guided ? Math.round((onMark / guided) * 100) : null,
  };
}

/**
 * One unit's docking performance, 0–1: whether it is guiding at all, times
 * how well it stops aircraft on the mark and whether its calibration is in
 * date. A unit that is down scores nothing however good its record — the
 * aircraft on that stand is being marshalled.
 *
 * Proposed weighting: 60% on the mark over the last day, 40% calibration in
 * date; a degraded unit counts at 70% of that.
 */
export function unitPerformance(u: VdgsUnit, now: Date): number | null {
  if (u.kind === "marshaller" || u.kind === "none" || u.status === "not_fitted") return null;
  if (!guides(u)) return 0;
  const st = unitStats(u, now);
  const accuracy = st.accuracy != null ? st.accuracy / 100 : 1;
  const cal = calibration(u, now).state;
  const inDate = cal === "ok" || cal === "soon" ? 1 : 0;
  const availability = u.status === "degraded" ? 0.7 : 1;
  return Math.round(availability * (0.6 * accuracy + 0.4 * inDate) * 100) / 100;
}

export interface ApronPerformance {
  apron: string;
  label: string;
  score: number | null;
  units: { unit: VdgsUnit; score: number }[];
}

/** Performance across all docking units, and apron by apron; the mean of each unit's. */
export function dockingPerformance(
  units: readonly VdgsUnit[],
  now: Date,
  stands: readonly Stand[] = [],
): { overall: number | null; units: number; aprons: ApronPerformance[] } {
  const scored = units
    .map((unit) => ({ unit, score: unitPerformance(unit, now) }))
    .filter((x): x is { unit: VdgsUnit; score: number } => x.score != null);
  const mean = (xs: number[]) =>
    xs.length ? Math.round((xs.reduce((a, b) => a + b, 0) / xs.length) * 100) / 100 : null;
  const aprons = new Map<string, { unit: VdgsUnit; score: number }[]>();
  for (const x of scored) {
    const k = x.unit.apron ?? stands.find((st) => st.code === x.unit.stand)?.zoneCode ?? "—";
    aprons.set(k, [...(aprons.get(k) ?? []), x]);
  }
  return {
    overall: mean(scored.map((x) => x.score)),
    units: scored.length,
    aprons: [...aprons.entries()].map(([apron, list]) => ({
      apron,
      label: APRON_LABEL[apron] ?? apron,
      score: mean(list.map((x) => x.score)),
      units: list,
    })),
  };
}
